import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { NOOR } from "@/constants/testIds/noor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 8000;

// Hoisted style + motion objects — referential stability avoids re-render churn.
const HEADLINE_INIT = { opacity: 0, y: 16 };
const HEADLINE_ANIM = { opacity: 1, y: 0 };
const HEADLINE_TRANS = { duration: 0.9 };
const HEADLINE_STYLE = { color: "var(--noor-bone)", fontSize: "clamp(2.4rem, 5vw, 4.6rem)" };
const HEADLINE_EM_STYLE = { color: "var(--noor-rosegold)" };
const HAIRLINE_TRACK_STYLE = { background: "var(--border-subtle)" };
const HAIRLINE_INIT = { x: "-50%", width: "50%" };
const HAIRLINE_ANIM = { x: "100%" };
const HAIRLINE_TRANS = { duration: 4.5, ease: "linear", repeat: Infinity };
const HAIRLINE_BAR_STYLE = { background: "var(--noor-rosegold)" };
const META_STYLE = {
  color: "var(--noor-vellum)",
  letterSpacing: "0.28em",
  textTransform: "uppercase",
};
const COPY_STYLE = {
  color: "var(--noor-vellum)",
  letterSpacing: "0.22em",
  textTransform: "uppercase",
};

// Queue token is now held server-side via the HttpOnly `noor_queue` cookie
// (issued by /api/queue/check). The browser carries it automatically when we
// post with `withCredentials: true`. No client-side storage required.

function WaitingHeadline({ position }) {
  return (
    <motion.h1
      initial={HEADLINE_INIT}
      animate={HEADLINE_ANIM}
      transition={HEADLINE_TRANS}
      className="font-display leading-[0.95] tracking-[-0.025em]"
      style={HEADLINE_STYLE}
    >
      You are{" "}
      <em
        data-testid={NOOR.waitingPosition}
        className="italic"
        style={HEADLINE_EM_STYLE}
      >
        №&nbsp;{String(position).padStart(3, "0")}
      </em>{" "}
      in line.
    </motion.h1>
  );
}

function WaitingHairline() {
  return (
    <div className="relative w-[260px] h-px overflow-hidden" style={HAIRLINE_TRACK_STYLE}>
      <motion.div
        className="absolute top-0 h-px"
        initial={HAIRLINE_INIT}
        animate={HAIRLINE_ANIM}
        transition={HAIRLINE_TRANS}
        style={HAIRLINE_BAR_STYLE}
      />
    </div>
  );
}

function WaitingMeta({ active, capacity, etaSeconds }) {
  return (
    <p className="font-mono text-[10px]" style={META_STYLE}>
      Active inside: {active} / {capacity}
      <span style={{ display: "inline-block", width: "1.2em" }} />·
      <span style={{ display: "inline-block", width: "1.2em" }} />
      Approx. wait: {Math.max(1, Math.round(etaSeconds / 60))} min
    </p>
  );
}

function WaitingScreen({ status }) {
  return (
    <section
      data-testid={NOOR.waitingRoom}
      className="min-h-[70vh] flex items-center justify-center px-6"
    >
      <div className="max-w-2xl text-center">
        <p className="overline mb-6">The House is at Capacity</p>
        <WaitingHeadline position={status.position} />
        <p className="mt-10 font-mono text-xs max-w-md mx-auto" style={COPY_STYLE}>
          The cellar will admit you shortly. Please do not refresh — your place is held.
        </p>
        <div className="mt-16 flex flex-col items-center gap-4">
          <WaitingHairline />
          <WaitingMeta active={status.active} capacity={status.capacity} etaSeconds={status.eta_seconds} />
        </div>
      </div>
    </section>
  );
}

// Polls /api/queue/check. Calls onAdmitted() once the position reaches 0.
// Renders nothing while admitted (parent decides) or the waiting screen otherwise.
export default function WaitingRoom({ onAdmitted, children }) {
  const [status, setStatus] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    let alive = true;

    const tick = async () => {
      try {
        const res = await axios.post(`${API}/queue/check`, {}, { withCredentials: true });
        if (!alive) return;
        const data = res.data;
        setStatus(data);
        if (data.admitted) {
          if (onAdmitted) onAdmitted();
          return; // stop polling
        }
      } catch {
        // Network blip — polling continues on schedule. Surface failures only
        // if they're sustained (a future enhancement would set a state flag
        // after N consecutive failures and render a "reconnecting…" notice).
      }
      if (alive) timerRef.current = setTimeout(tick, POLL_MS);
    };

    tick();
    return () => {
      alive = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [onAdmitted]);

  // If we haven't received first status — defer to parent.
  if (!status) return null;
  if (status.admitted) return children || null;
  return <WaitingScreen status={status} />;
}
